import React, { useState } from "react";
import { View, StyleSheet, TouchableOpacity, Platform, Linking } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { PaperclipIcon, LinkSimpleIcon } from "@phosphor-icons/react";
import { ProjectSubmission } from "@axio/graphql/generated/graphql";
import useFileFetch from "../../../hooks/useFileFetch";

interface ProjectSubmittedSectionProps {
  submission: ProjectSubmission;
}

const ProjectSubmittedSection: React.FC<ProjectSubmittedSectionProps> = ({ submission }) => {
  const [showSubmissionDetails, setShowSubmissionDetails] = useState(false);
  const { fetchAndOpenFile } = useFileFetch();

  return (
    <View style={styles.submittedFilesSection}>
      <CustomText
        style={styles.submittedFilesTitle}
        text="Project submitted - awaiting review"
        size="s"
        weight="bold"
      />
      <CustomSpacing type="vertical" size="xs" />
      <CustomText
        style={styles.submittedFilesDescription}
        text="We received your submission. Your teacher will review it soon. You can still add notes for clarifications."
        size="s"
        weight="normal"
      />
      <CustomSpacing type="vertical" size="s" />
      {!showSubmissionDetails ? (
        <TouchableOpacity onPress={() => setShowSubmissionDetails(true)} style={styles.expandButton}>
          <CustomText text="See what I submitted" style={styles.expandButtonText} />
        </TouchableOpacity>
      ) : (
        <View style={styles.submissionDetailsContainer}>
          <CustomSpacing type="vertical" size="xl" />
          <CustomText text="What you submitted" style={styles.submissionDetailsTitle} size="s" weight="bold" />
          <CustomText
            text={
              submission?.completedAt
                ? `Submitted at ${new Date(submission.completedAt).toLocaleString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: true,
                  })}`
                : ""
            }
            style={styles.submissionLink}
            useTranslationText={false}
          />
          <CustomSpacing type="vertical" size="s" />
          {(submission?.files?.length > 0 || submission?.submissionLink) && (
            <View style={styles.submissionFilesContainer}>
              {submission?.submissionLink && (
                <TouchableOpacity
                  style={styles.submissionFileItem}
                  onPress={() => {
                    if (Platform.OS === "web") {
                      window.open(submission.submissionLink, "_blank");
                    } else {
                      Linking.openURL(submission.submissionLink);
                    }
                  }}>
                  <LinkSimpleIcon size={16} color="#344054" weight="regular" />
                  <CustomText
                    text={submission.submissionLink}
                    style={styles.submissionFileName}
                    useTranslationText={false}
                  />
                </TouchableOpacity>
              )}
              {submission?.files?.map((file) => (
                <TouchableOpacity
                  key={file.id}
                  style={styles.submissionFileItem}
                  onPress={() => fetchAndOpenFile(file.fileURL)}>
                  <PaperclipIcon size={16} color="#344054" weight="regular" />
                  <CustomText
                    text={file.fileName || file.id}
                    style={styles.submissionFileName}
                    useTranslationText={false}
                  />
                </TouchableOpacity>
              ))}
            </View>
          )}
          {submission?.submissionNotes && (
            <>
              <CustomSpacing type="vertical" size="s" />
              <CustomText text="Notes" style={styles.submissionDetailsTitle} size="s" weight="bold" />
              <CustomSpacing type="vertical" size="xs" />
              <CustomText
                text={submission.submissionNotes}
                style={styles.submissionNotesText}
                useTranslationText={false}
              />
            </>
          )}
          <CustomSpacing type="vertical" size="xl" />
          <TouchableOpacity onPress={() => setShowSubmissionDetails(false)} style={styles.expandButton}>
            <CustomText text="Show less" style={styles.expandButtonText} />
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  submittedFilesSection: {
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#EAECF0",
    backgroundColor: "#FFF",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    marginTop: 30,
  },
  submittedFilesTitle: {
    color: "#0F172B",
  },
  submittedFilesDescription: {
    color: "#667085",
  },
  expandButton: {
    paddingVertical: 4,
  },
  expandButtonText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#3E68FE",
  },
  submissionDetailsContainer: {
    width: "100%",
  },
  submissionDetailsTitle: {
    color: "#344054",
  },
  submissionFilesContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: 8,
    width: "100%",
  },
  submissionFileItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    minWidth: 155.75,
    paddingVertical: 3.395,
    paddingHorizontal: 10,
    justifyContent: "center",
    gap: 5,
    borderRadius: 360,
    backgroundColor: "#F8FAFC",
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  submissionFileName: {
    fontSize: 14,
    fontWeight: "400",
    color: "#344054",
    flex: 1,
  },
  submissionLink: {
    fontSize: 14,
    fontWeight: "400",
    color: "#667085",
    lineHeight: 20,
  },
  submissionNotesText: {
    fontSize: 14,
    fontWeight: "400",
    color: "#667085",
    lineHeight: 20,
  },
});

export default ProjectSubmittedSection;
